import { useEffect } from 'react'
import { toast } from '../utils/toast'
import { websocketService } from '../services/websocketService'
import { useWebSocketStore } from '../store/websocketStore'
import { useNotificationStore } from '../store/notificationStore'
import { useAuthStore } from '../store/authStore'
import { browserNotificationService } from '../services/browserNotificationService'
import { apiClient } from '../services/apiClient'
import * as indexedDB from '../services/indexedDBService'
import type { Notificacao } from '../services/notificacaoService'
import type { Pedido } from '../types'

/**
 * Componente que gerencia a conexão WebSocket global
 * Deve ser montado uma única vez no App.tsx
 */
export function WebSocketProvider() {
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (!isAuthenticated) {
      console.log('❌ [WebSocketProvider] Usuário não autenticado, não conectando')
      return
    }

    console.log('🔌 [WebSocketProvider] Iniciando conexão WebSocket GLOBAL...')

    // Conecta ao WebSocket
    websocketService.connect()

    // Aguarda conexão antes de registrar listeners
    const timer = setTimeout(() => {
      console.log('⏰ [WebSocketProvider] Registrando listeners GLOBAIS (uma única vez)...')
      console.log('🔗 WebSocket conectado?', websocketService.isConnected())
      console.log('🆔 Socket ID:', websocketService.getSocketId())

      // Listener para status de pedido alterado
      websocketService.onPedidoStatusAlterado(async (data) => {
        console.log('📦 [WebSocketProvider] Status do pedido alterado:', data)
        
        // Atualiza cache local (IndexedDB) com dados mais recentes
        try {
          console.log('💾 [WebSocket] Atualizando cache do pedido:', data.pedidoId)
          const response = await apiClient.get<Pedido>(`/pedidos/${data.pedidoId}`)
          await indexedDB.put(indexedDB.STORES.PEDIDOS_PENDENTES, response.data)
          console.log('✅ [WebSocket] Cache do pedido atualizado com sucesso')
        } catch (error) {
          console.error('❌ [WebSocket] Erro ao atualizar cache do pedido:', error)
        }
        
        // Dispara callbacks registrados por componentes
        useWebSocketStore.getState().triggerPedidoStatusAlterado(data)
        
        // Mapeia severity
        let severity: 'success' | 'info' | 'warning' | 'error' = 'info'
        if (data.statusNovo === 'concluido') severity = 'success'
        else if (data.statusNovo === 'cancelado') severity = 'error'
        else if (data.statusNovo === 'processando') severity = 'warning'
        
        const titulo = '📦 Status do Pedido Alterado'
        const mensagem = `${data.clienteNome}: ${data.statusAntigo} → ${data.statusNovo}`
        
        // Cria notificação local
        const novaNotificacao: Notificacao = {
          id: `local-${Date.now()}`,
          userId: useAuthStore.getState().user?.id || '',
          tipo: 'status_alterado',
          titulo,
          mensagem,
          lida: false,
          pedidoId: data.pedidoId,
          createdAt: new Date().toISOString(),
        }
        
        // Adiciona à store global
        useNotificationStore.getState().addNotification(novaNotificacao)
        
        // Exibe toast visual na tela
        if (severity === 'success') {
          toast.success(titulo, { description: mensagem, duration: 3000 })
        } else if (severity === 'error') {
          toast.error(titulo, { description: mensagem, duration: 3000 })
        } else if (severity === 'warning') {
          toast.warning(titulo, { description: mensagem, duration: 3000 })
        } else {
          toast.info(titulo, { description: mensagem, duration: 3000 })
        }
        
        // Exibe notificação do browser
        if (browserNotificationService.isEnabled()) {
          browserNotificationService.notify(titulo, mensagem, severity)
        }
      })

      // Listener para pedido criado
      websocketService.onPedidoCriado(async (data) => {
        console.log('🆕 [WebSocketProvider] Novo pedido criado:', data)
        
        // Atualiza cache local (IndexedDB) adicionando o novo pedido
        try {
          console.log('💾 [WebSocket] Adicionando novo pedido ao cache:', data.pedidoId)
          const response = await apiClient.get<Pedido>(`/pedidos/${data.pedidoId}`)
          await indexedDB.put(indexedDB.STORES.PEDIDOS_PENDENTES, response.data)
          console.log('✅ [WebSocket] Novo pedido adicionado ao cache com sucesso')
        } catch (error) {
          console.error('❌ [WebSocket] Erro ao adicionar pedido ao cache:', error)
        }
        
        // Dispara callbacks registrados por componentes
        useWebSocketStore.getState().triggerPedidoCriado(data)
        
        const titulo = '🎉 Novo Pedido Criado'
        const valor = data.valorTotal 
          ? ` - R$ ${data.valorTotal.toFixed(2)}` 
          : ''
        const mensagem = `${data.clienteNome}${valor}`
        
        // Cria notificação local
        const novaNotificacao: Notificacao = {
          id: `local-${Date.now()}`,
          userId: useAuthStore.getState().user?.id || '',
          tipo: 'pedido_criado',
          titulo,
          mensagem,
          lida: false,
          pedidoId: data.pedidoId,
          createdAt: new Date().toISOString(),
        }
        
        // Adiciona à store global
        useNotificationStore.getState().addNotification(novaNotificacao)
        
        // Exibe toast visual na tela
        toast.success(titulo, { description: mensagem, duration: 5000 })
        
        // Exibe notificação do browser
        if (browserNotificationService.isEnabled()) {
          browserNotificationService.notify(titulo, mensagem, 'success')
        }
      })

      // Listener para pedido cancelado
      websocketService.onPedidoCancelado(async (data) => {
        console.log('❌ [WebSocketProvider] Pedido cancelado:', data)
        
        // Atualiza cache local (IndexedDB) com status cancelado
        try {
          console.log('💾 [WebSocket] Atualizando pedido cancelado no cache:', data.pedidoId)
          const response = await apiClient.get<Pedido>(`/pedidos/${data.pedidoId}`)
          await indexedDB.put(indexedDB.STORES.PEDIDOS_PENDENTES, response.data)
          console.log('✅ [WebSocket] Pedido cancelado atualizado no cache')
        } catch (error) {
          console.error('❌ [WebSocket] Erro ao atualizar pedido cancelado no cache:', error)
        }
        
        // Dispara callbacks registrados por componentes
        useWebSocketStore.getState().triggerPedidoCancelado(data)
        
        const titulo = '❌ Pedido Cancelado'
        const motivo = data.motivo ? ` - ${data.motivo}` : ''
        const mensagem = `${data.clienteNome}${motivo}`
        
        // Cria notificação local
        const novaNotificacao: Notificacao = {
          id: `local-${Date.now()}`,
          userId: useAuthStore.getState().user?.id || '',
          tipo: 'status_alterado',
          titulo,
          mensagem,
          lida: false,
          pedidoId: data.pedidoId,
          createdAt: new Date().toISOString(),
        }
        
        // Adiciona à store global
        useNotificationStore.getState().addNotification(novaNotificacao)
        
        // Exibe toast visual na tela
        toast.error(titulo, { description: mensagem, duration: 3000 })
        
        // Exibe notificação do browser
        if (browserNotificationService.isEnabled()) {
          browserNotificationService.notify(titulo, mensagem, 'error')
        }
      })

      // Listener para mensagens do sistema
      websocketService.onSistemaMensagem((data) => {
        console.log('📢 [WebSocketProvider] Mensagem do sistema:', data)
        
        const severityMap = {
          'manutencao': 'error' as const,
          'alerta': 'warning' as const,
          'atualizacao': 'info' as const,
          'info': 'info' as const,
        }
        
        const severity = severityMap[data.tipo]
        
        // Cria notificação local
        const novaNotificacao: Notificacao = {
          id: `local-${Date.now()}`,
          userId: useAuthStore.getState().user?.id || '',
          tipo: 'sistema',
          titulo: data.titulo,
          mensagem: data.mensagem,
          lida: false,
          createdAt: new Date().toISOString(),
        }
        
        // Adiciona à store global
        useNotificationStore.getState().addNotification(novaNotificacao)
        
        // Exibe toast visual na tela
        if (severity === 'error') {
          toast.error(data.titulo, { description: data.mensagem, duration: 5000 })
        } else if (severity === 'warning') {
          toast.warning(data.titulo, { description: data.mensagem, duration: 5000 })
        } else {
          toast.info(data.titulo, { description: data.mensagem, duration: 5000 })
        }
        
        // Exibe notificação do browser
        if (browserNotificationService.isEnabled()) {
          browserNotificationService.notify(data.titulo, data.mensagem, severity)
        }
      })

      // Listener para erros
      websocketService.onError((data) => {
        console.error('❌ [WebSocketProvider] Erro do WebSocket:', data)
        
        if (browserNotificationService.isEnabled()) {
          browserNotificationService.notify('❌ Erro', data.message, 'error')
        }
      })

      console.log('✅ [WebSocketProvider] Todos os listeners globais registrados')
    }, 1000)

    // Cleanup: Desconecta apenas quando usuário desloga
    return () => {
      clearTimeout(timer)
      console.log('🔌 [WebSocketProvider] Desconectando WebSocket (logout)...')
      websocketService.off('pedido:status_alterado')
      websocketService.off('pedido:criado')
      websocketService.off('pedido:cancelado')
      websocketService.off('sistema:mensagem')
      websocketService.off('error')
      websocketService.disconnect()
      
      // Limpa todos os callbacks registrados
      useWebSocketStore.getState().clearAll()
    }
  }, [isAuthenticated]) // Só reconecta se o estado de autenticação mudar

  // Componente não renderiza nada
  return null
}
